import { formatDate } from '../../../../helpers/formatDate'
import {
  getStoreRelationBrandEntries,
  requiresStoreRelationPrincipalReassignment,
  resolveStoreRelationAssignmentStatus
} from '../../helpers/storeRelationAssignmentUtils'
import {
  STORE_RELATIONS_FALLBACK_TEXT,
  STORE_RELATIONS_STATUS_UI_MAP
} from '../../constants/storeConstants'
import {
  formatStoreRelationSalesmanLabel,
  getStoreRelationRelationSalesmanKey,
  resolveStoreRelationText
} from '../../utils/storeRelationDisplayUtils'
import {
  STORE_RELATION_SHOW_BRANDLESS_LABEL,
  STORE_RELATION_SHOW_REASSIGNMENT_WARNING_MESSAGES
} from '../constants/storeShowConstants'

export const buildStoreRelationUniqueBrandLabels = (brandEntries = []) => {
  const seenLabels = new Set()

  return (Array.isArray(brandEntries) ? brandEntries : [])
    .map((item) => {
      if (item?.brand?.name) return item.brand.name
      if (item?.brand?.db_ref) return item.brand.db_ref

      if (item?.active === true && item?.status === 'active') {
        return STORE_RELATION_SHOW_BRANDLESS_LABEL
      }

      return null
    })
    .filter((label) => {
      if (!label || seenLabels.has(label)) return false

      seenLabels.add(label)
      return true
    })
}

export const buildStoreRelationBrandLabels = (relation) => {
  return buildStoreRelationUniqueBrandLabels(getStoreRelationBrandEntries(relation))
}

export const buildStoreRelationShowUserRows = (storeRelation) => {
  const groupedUsers = new Map()

  ;(storeRelation?.client_brands || []).forEach((relation, index) => {
    const salesman = relation?.salesman
    if (!salesman) return

    const key = getStoreRelationRelationSalesmanKey(relation, `salesman-${index}`)
    const currentUser = groupedUsers.get(key) || {
      key,
      userLabel: formatStoreRelationSalesmanLabel(salesman),
      brands: [],
      relations: []
    }

    buildStoreRelationBrandLabels(relation).forEach((brandLabel) => {
      if (!currentUser.brands.includes(brandLabel)) {
        currentUser.brands.push(brandLabel)
      }
    })

    currentUser.relations.push(relation)
    groupedUsers.set(key, currentUser)
  })

  return Array.from(groupedUsers.values())
    .map((user) => ({
      key: user.key,
      userLabel: user.userLabel,
      brands: user.brands,
      ...resolveStoreRelationAssignmentStatus(user.relations)
    }))
    .filter((user) => user.brands.length > 0)
}

export const buildStoreRelationShowDetailsData = (storeRelation, userRows = []) => {
  const principalStatus = storeRelation?.client_principal?.status || ''
  const principalSalesmanLabel = formatStoreRelationSalesmanLabel(storeRelation?.client_principal?.salesman)
  const principalStatusLabel =
    STORE_RELATIONS_STATUS_UI_MAP?.[principalStatus]?.label ||
    principalStatus ||
    STORE_RELATIONS_FALLBACK_TEXT
  const createdAt =
    storeRelation?.created_at ||
    storeRelation?.create_at ||
    storeRelation?.client_principal?.created_at ||
    storeRelation?.client_principal?.create_at ||
    null
  const updatedAt =
    storeRelation?.updated_at ||
    storeRelation?.update_at ||
    storeRelation?.client_principal?.updated_at ||
    storeRelation?.client_principal?.update_at ||
    storeRelation?.client_principal?.suspended_at ||
    null

  return {
    client_name: resolveStoreRelationText(storeRelation?.client?.name),
    client_db_ref: resolveStoreRelationText(storeRelation?.client?.db_ref),
    principal_salesman_name: principalSalesmanLabel,
    createdAt,
    assigned_users_label: principalSalesmanLabel,
    users_count: userRows.length || STORE_RELATIONS_FALLBACK_TEXT,
    principal_status_label: principalStatusLabel,
    created_at_label: createdAt ? formatDate(createdAt) : STORE_RELATIONS_FALLBACK_TEXT,
    updated_at_label: updatedAt ? formatDate(updatedAt) : STORE_RELATIONS_FALLBACK_TEXT,
    client_supplier_name: resolveStoreRelationText(storeRelation?.client_configuration?.supplier_name),
    client_buyer_name: resolveStoreRelationText(storeRelation?.client_configuration?.buyer_name),
    client_reference_name: resolveStoreRelationText(storeRelation?.client_configuration?.store_reference)
  }
}

export const filterStoreRelationShowUserRows = (userRows = [], searchInput = '') => {
  const normalizedSearch = searchInput.trim().toLowerCase()

  if (!normalizedSearch) return userRows

  return userRows.filter((row) => {
    const brandText = row.brands.join(' ').toLowerCase()

    return (
      row.userLabel.toLowerCase().includes(normalizedSearch) ||
      brandText.includes(normalizedSearch)
    )
  })
}

export const buildStoreRelationBrandValidationRows = (storeRelation) => {
  const groupedRows = new Map()

  ;(storeRelation?.client_brands || []).forEach((relation, index) => {
    const salesmanKey = getStoreRelationRelationSalesmanKey(relation, `salesman-${index}`)
    const currentRow = groupedRows.get(salesmanKey) || {
      salesmanDbRef: relation?.salesman?.db_ref || '',
      userLabel: formatStoreRelationSalesmanLabel(relation?.salesman),
      brandDbRefs: []
    }

    getStoreRelationBrandEntries(relation)
      .map((brandEntry) => brandEntry?.brand)
      .filter(Boolean)
      .forEach((brand) => {
        const brandDbRef = brand?.db_ref || brand?.name

        if (brandDbRef && !currentRow.brandDbRefs.includes(brandDbRef)) {
          currentRow.brandDbRefs.push(brandDbRef)
        }
      })

    groupedRows.set(salesmanKey, currentRow)
  })

  return Array.from(groupedRows.values()).filter((row) => row.salesmanDbRef && row.brandDbRefs.length > 0)
}

export const buildStoreRelationUsersRequiringReassignmentLabels = (storeRelation) => {
  const groupedRows = new Map()

  ;(storeRelation?.client_brands || []).forEach((relation, index) => {
    const salesman = relation?.salesman
    if (!salesman) return

    const key = getStoreRelationRelationSalesmanKey(relation, `salesman-${index}`)
    const currentRow = groupedRows.get(key) || {
      userLabel: formatStoreRelationSalesmanLabel(salesman),
      relations: []
    }

    currentRow.relations.push(relation)
    groupedRows.set(key, currentRow)
  })

  return Array.from(groupedRows.values())
    .filter((row) => resolveStoreRelationAssignmentStatus(row.relations).requiresReassignment)
    .map((row) => row.userLabel)
    .filter(Boolean)
}

export const buildStoreRelationReassignmentWarningMessages = (storeRelation) => {
  const warningMessages = []

  if (requiresStoreRelationPrincipalReassignment(storeRelation)) {
    warningMessages.push(STORE_RELATION_SHOW_REASSIGNMENT_WARNING_MESSAGES.PRINCIPAL)
  }

  const usersRequiringReassignmentLabels = buildStoreRelationUsersRequiringReassignmentLabels(storeRelation)

  if (usersRequiringReassignmentLabels.length > 0) {
    warningMessages.push(
      `${STORE_RELATION_SHOW_REASSIGNMENT_WARNING_MESSAGES.USERS_PREFIX}${usersRequiringReassignmentLabels.join(' | ')}`
    )
  }

  return warningMessages
}
